import type { EventProperties } from '@/lib/analytics'
import { resolveInstantAddDeleteTarget } from '@/components/projects/instantAdd/instantAddState'
import {
  track,
  trackActivationMilestone,
  trackFeatureUsed,
} from '@/lib/analytics'
import { requestBlob } from '@/lib/api/base-api'
import { noteEngagementActivity } from '@/lib/engagement/activity-signal'
import { createLogger } from '@/lib/logger'
import { withStatusCommentRetry } from '@/lib/tasks/status-comment'

import {
  CreateTask,
  DeletedTaskGroup,
  DuplicateResult,
  DuplicateTaskGroupToProject,
  ExportOptions,
  ImportOptions,
  ImportPreviewRecord,
  ImportResult,
  MergeTasksPreview,
  MergeTasksRequest,
  MergeTasksResponse,
  ModuleTypeEnum,
  MovePreview,
  MoveResult,
  MoveTaskGroup,
  normalizeRecurringPattern,
  PaginatedResponse,
  PaginationParams,
  RecurringPattern,
  RestoreTaskGroupResult,
  Task,
  TaskCompletedCounts,
  TaskGroup,
  TaskGroupWithCounts,
  TaskStatusTypeEnum,
  TaskWithAssignments,
  UpdateTaskInput,
} from '@scrambl/shared'
import { assertTaskWriteAcknowledgement } from '@scrambl/shared/offline'

import { apiClient } from './api-client'

const logger = createLogger('lib/api/tasks.api')

// Helper function to clean and normalize API parameters
function cleanParams(params: Record<string, unknown>): Record<string, string> {
  const cleaned: Record<string, string> = {}

  Object.keys(params).forEach(key => {
    const value = params[key]
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value) && value.length > 0) {
        cleaned[key] = value.join(',')
      } else if (typeof value === 'object') {
        cleaned[key] = JSON.stringify(value)
      } else {
        cleaned[key] = String(value)
      }
    }
  })

  return cleaned
}

// Helper function to normalize a list of tasks
const normalizeTasksList = (
  tasks: TaskWithAssignments[],
): TaskWithAssignments[] => {
  if (!tasks) return []
  return tasks.map(task => normalizeTaskDates(task))
}

// Helper function to properly serialize dates in task objects
const normalizeTaskDates = (task: TaskWithAssignments): TaskWithAssignments => {
  if (!task) return task

  const result: TaskWithAssignments = { ...task }
  const taskWithDeletedAt = result as TaskWithAssignments & {
    deletedAt?: Date | string | null
  }

  // Ensure dates are properly formatted
  if (result.scheduledFor !== undefined) {
    result.scheduledFor = result.scheduledFor
      ? new Date(result.scheduledFor)
      : null
  }

  if (result.dueDate !== undefined) {
    result.dueDate = result.dueDate ? new Date(result.dueDate) : null
  }

  if (result.startedAt !== undefined) {
    result.startedAt = result.startedAt ? new Date(result.startedAt) : null
  }

  if (result.completedAt !== undefined) {
    result.completedAt = result.completedAt
      ? new Date(result.completedAt)
      : null
  }

  if (result.createdAt !== undefined) {
    result.createdAt = new Date(result.createdAt)
  }

  if (result.updatedAt !== undefined) {
    result.updatedAt = new Date(result.updatedAt)
  }

  if (taskWithDeletedAt.deletedAt !== undefined) {
    taskWithDeletedAt.deletedAt = taskWithDeletedAt.deletedAt
      ? new Date(taskWithDeletedAt.deletedAt)
      : null
  }

  // Handle recurring task dates
  if (result.recurringEndDate !== undefined) {
    result.recurringEndDate = result.recurringEndDate
      ? new Date(result.recurringEndDate)
      : null
  }

  if (result.nextRecurrenceDate !== undefined) {
    result.nextRecurrenceDate = result.nextRecurrenceDate
      ? new Date(result.nextRecurrenceDate)
      : null
  }

  result.recurringPattern = normalizeRecurringPattern(result.recurringPattern)

  // Map taskAssignments to assignees format for UI components
  if (result.taskAssignments && Array.isArray(result.taskAssignments)) {
    result.assignees = result.taskAssignments.map(assignment => ({
      user: assignment.user
        ? {
            id: assignment.userId || assignment.user.id,
            userId: assignment.userId || assignment.user.id,
            memberId: assignment.memberId ?? null,
            // Compute name consistently with backend: displayName || `${firstName} ${lastName}`
            name:
              assignment.user.displayName ||
              (assignment.user.firstName || assignment.user.lastName
                ? `${assignment.user.firstName || ''} ${assignment.user.lastName || ''}`.trim()
                : assignment.user.email?.split('@')[0] || 'User'),
            email: assignment.user.email || '',
            avatarUrl: assignment.user.image,
          }
        : {
            id: assignment.userId || assignment.memberId || '',
            userId: assignment.userId ?? null,
            memberId: assignment.memberId ?? null,
            name: `User ${
              (assignment.userId || assignment.memberId)?.substring(0, 8) ||
              'Unknown'
            }`,
            email: '',
            avatarUrl: undefined,
          },
    }))

    // Drop duplicate rows for one person (two task_assignments rows for the
    // same user, or a userId-keyed row beside a memberId-only row).
    const seenAssigneeKeys = new Set<string>()
    result.assignees = result.assignees.filter(assignee => {
      const key =
        assignee.user?.email?.trim().toLowerCase() ||
        assignee.user?.userId ||
        assignee.user?.id ||
        ''
      if (key && seenAssigneeKeys.has(key)) return false
      if (key) seenAssigneeKeys.add(key)
      return true
    })

    // Sort assignees alphabetically by user name
    result.assignees.sort((a, b) => {
      const nameA = a.user?.name || a.user?.email || ''
      const nameB = b.user?.name || b.user?.email || ''
      return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' })
    })
  } else {
    // Always ensure assignees is an array
    result.assignees = result.assignees || []
  }

  // Ensure dependencies are properly initialized - preserve existing data
  if (!result.dependenciesFrom || !Array.isArray(result.dependenciesFrom)) {
    result.dependenciesFrom = task.dependenciesFrom || []
  }
  if (!result.dependenciesTo || !Array.isArray(result.dependenciesTo)) {
    result.dependenciesTo = task.dependenciesTo || []
  }

  // Preserve project settings
  if (task.projectSettings) {
    result.projectSettings = task.projectSettings
  }

  // Flatten custom fields from nested customFields object to top-level with custom_ prefix
  if (result.customFields && typeof result.customFields === 'object') {
    const resultRecord = result as Record<string, unknown>
    Object.entries(result.customFields).forEach(([fieldName, value]) => {
      const flattenedKey = `custom_${fieldName}`
      resultRecord[flattenedKey] = value
    })
  }

  return result
}

// Interface definitions
interface PaginatedTaskGroupResponse {
  data: TaskGroup[]
  meta: {
    totalItems: number
    currentPage: number
    itemsPerPage: number
    totalPages: number
    hasNextPage: boolean
    hasPreviousPage: boolean
  }
}

interface PaginatedTaskGroupWithCountsResponse {
  data: TaskGroupWithCounts[]
  meta: {
    totalItems: number
    currentPage: number
    itemsPerPage: number
    totalPages: number
    hasNextPage: boolean
    hasPreviousPage: boolean
  }
}

export interface AssignablePortalMember {
  userId: string
  name: string
  email: string
  image: string | null
}

export interface AssignablePortalMembers {
  entityId: string | null
  taskVisibleToClient: boolean
  members: AssignablePortalMember[]
}

export interface TaskApprovalEntry {
  id: string
  action:
    | 'SUBMITTED'
    | 'APPROVED'
    | 'REJECTED'
    | 'CHANGES_REQUESTED'
    | 'RECALLED'
  previousStatus: string | null
  newStatus: string | null
  performedBy: string
  reason: string | null
  performedAt: string
  performer?: {
    id: string
    firstName: string | null
    lastName: string | null
    displayName: string | null
    email: string
    image: string | null
  }
}

// Product analytics helpers. Kept in the service layer so every surface that
// creates or completes a task is counted, not just the ones wired through
// useTaskMutations. Properties stay low-cardinality — never the task title.
function trackTaskCreated(
  task: {
    moduleType?: ModuleTypeEnum | null
    parentTaskId?: string | null
    priority?: string | null
  },
  extra?: { is_subtask?: boolean; count?: number; source?: string },
): void {
  track('task_created', {
    module: task.moduleType ?? 'none',
    priority: task.priority ?? null,
    ...extra,
  })
  trackActivationMilestone('task_created', {
    module: task.moduleType ?? 'none',
  })
}

function trackTaskCompleted(properties: EventProperties): void {
  track('task_completed', properties)
}

// API functions
export const tasksApi = {
  // Unified task retrieval methods
  getTasks: async (
    params: PaginationParams & {
      moduleType?: string
      moduleId?: string
      groupId?: string
      groupIds?: string[]
      taskTypeDefIds?: string[]
      cycleIds?: string[]
      search?: string
      priority?: string
      priorities?: string[]
      assigneeIds?: string[]
      entityIds?: string[]
      /** A client's Work: its active projects' tasks and its own tasks. */
      workEntityId?: string
      projectIds?: string[]
      dueDateFrom?: string
      dueDateTo?: string
      scheduledForFrom?: string
      scheduledForTo?: string
      createdAtFrom?: string
      createdAtTo?: string
      statuses?: string[]
      sortBy?: string
      sortOrder?: 'asc' | 'desc'
      includeSubtasks?: boolean
      includeAssignees?: boolean
      includeModuleInfo?: boolean
      includeCustomFields?: boolean
      includeDependencies?: boolean
      includePersonalTasks?: boolean
      sprintId?: string | null
      noSprint?: boolean // When true, only return tasks with no sprint (backlog)
      bucketId?: string | null
      noBucket?: boolean // When true, only return tasks with no bucket
      excludeCompleted?: boolean
      /** Only completed/canceled tasks (inverse of excludeCompleted) */
      onlyCompleted?: boolean
      /** Hide tasks in closed projects and under closed parents (My Work) */
      excludeClosedContainers?: boolean
      noGroup?: boolean // When true, only tasks in no task group
      noCycle?: boolean // When true, only tasks outside any cycle
      excludePrivate?: boolean // Keep private tasks out even with assignee filters
      statusNames?: string[] // Status filter by display name
      query?: string // ScQL query string (Jira-style)
      view?: 'minimal' | 'list' | 'board' | 'full'
    },
    options?: { signal?: AbortSignal },
  ): Promise<PaginatedResponse<TaskWithAssignments>> => {
    const normalizedParams = { ...params }
    const isMyTasksListRequest =
      Array.isArray(normalizedParams.assigneeIds) &&
      normalizedParams.assigneeIds.length > 0 &&
      normalizedParams.includePersonalTasks === true &&
      normalizedParams.includeSubtasks === true &&
      normalizedParams.view === 'list'

    // My Tasks views use client-side bucketing/sorting and only need a stable,
    // freshness-first fetch. Avoid dueDate asc + pagination truncation that can
    // hide newly created tasks when there are many historical records.
    if (
      isMyTasksListRequest &&
      normalizedParams.sortBy === 'dueDate' &&
      normalizedParams.sortOrder === 'asc'
    ) {
      normalizedParams.sortBy = 'updatedAt'
      normalizedParams.sortOrder = 'desc'
    }

    const cleanedParams = cleanParams(normalizedParams)

    // Date-filtered lists must match each task's own dates, including subtasks
    // whose parent falls outside the range. Keep the ordinary root-only list
    // default, and honor callers that explicitly request roots or all tasks.
    const includeSubtasks =
      normalizedParams.includeSubtasks ??
      Boolean(
        normalizedParams.dueDateFrom ||
        normalizedParams.dueDateTo ||
        normalizedParams.scheduledForFrom ||
        normalizedParams.scheduledForTo ||
        normalizedParams.createdAtFrom ||
        normalizedParams.createdAtTo,
      )
    if (!includeSubtasks) {
      cleanedParams.excludeSubtasks = 'true'
    }

    const queryParams = new URLSearchParams(cleanedParams)

    const response = await apiClient<PaginatedResponse<TaskWithAssignments>>(
      `/tasks?${queryParams}`,
      {
        method: 'GET',
        signal: options?.signal,
      },
    )

    return {
      ...response,
      data: response.data.map(normalizeTaskDates),
    }
  },

  // Get a single task by ID
  getTask: async (
    id: string,
    includeCustomFields = true,
  ): Promise<TaskWithAssignments> => {
    const url = `/tasks/${id}?includeCustomFields=${includeCustomFields}`
    const response = await apiClient<TaskWithAssignments>(url, {
      method: 'GET',
    })

    return normalizeTaskDates(response)
  },

  // Get task by task number
  getTaskByNumber: async (taskNumber: string): Promise<TaskWithAssignments> => {
    const url = `/tasks/by-number/${encodeURIComponent(taskNumber)}`
    const response = await apiClient<TaskWithAssignments>(url, {
      method: 'GET',
    })

    return normalizeTaskDates(response)
  },

  // Unified task creation
  createTask: async (
    task: CreateTask,
    options?: { signal?: AbortSignal },
  ): Promise<TaskWithAssignments> => {
    const response = await apiClient<TaskWithAssignments>('/tasks', {
      method: 'POST',
      body: task,
      signal: options?.signal,
    })

    // Instrumented here rather than at the button: tasks are created from the
    // draft row, the create dialog, the tray popover, the board and quick
    // capture, and several of those bypass useCreateTaskMutation.
    trackTaskCreated(task, { is_subtask: !!task.parentTaskId })

    return normalizeTaskDates(response)
  },

  // Create subtask (convenience method)
  createSubtask: async (
    parentTaskId: string,
    subtask: CreateTask,
  ): Promise<TaskWithAssignments> => {
    const response = await apiClient<TaskWithAssignments>('/tasks', {
      method: 'POST',
      body: {
        ...subtask,
        parentTaskId,
      },
    })

    trackTaskCreated(subtask, { is_subtask: true })

    return normalizeTaskDates(response)
  },

  // Update task
  updateTask: async (
    id: string,
    task: UpdateTaskInput,
  ): Promise<TaskWithAssignments> => {
    const { statusComment, ...fields } = task
    const method =
      Object.keys(fields).length === 1 || fields.customFields ? 'PATCH' : 'PUT'

    // A status in the update may require a comment (SD-145).
    const send = (comment: string | undefined) =>
      apiClient<Task>(`/tasks/${id}`, {
        method,
        body: comment ? { ...fields, statusComment: comment } : fields,
      })
    const response =
      fields.status !== undefined && fields.expectedRevision === undefined
        ? await withStatusCommentRetry(send, { statusComment })
        : await send(statusComment)

    assertTaskWriteAcknowledgement(response, id, fields.expectedRevision)
    return normalizeTaskDates(response)
  },

  moveTaskToModule: async (
    id: string,
    moduleType: ModuleTypeEnum,
    moduleId: string,
  ): Promise<TaskWithAssignments> => {
    const response = await apiClient<TaskWithAssignments>(`/tasks/${id}/move`, {
      method: 'PUT',
      body: { moduleType, moduleId },
    })
    return normalizeTaskDates(response)
  },

  // Promote a subtask to a standalone top-level task
  promoteSubtask: async (id: string): Promise<TaskWithAssignments> => {
    const response = await apiClient<TaskWithAssignments>(
      `/tasks/${id}/promote`,
      { method: 'POST' },
    )
    return normalizeTaskDates(response)
  },

  // Convert a standalone task into a subtask of a (same-project) parent task
  convertToSubtask: async (
    id: string,
    parentTaskId: string,
  ): Promise<TaskWithAssignments> => {
    const response = await apiClient<TaskWithAssignments>(
      `/tasks/${id}/convert-to-subtask`,
      { method: 'POST', body: { parentTaskId } },
    )
    return normalizeTaskDates(response)
  },

  // Delete task
  deleteTask: async (id: string): Promise<void> => {
    // Instant-add rows stay keyed by their temp id until a refetch settles
    // them, so deletes may arrive with a temp id — translate to the real id
    // and terminate the session (otherwise the session's cache defender
    // resurrects the pruned row).
    const apiId = resolveInstantAddDeleteTarget(id)
    if (!apiId) return
    await apiClient(`/tasks/${apiId}`, {
      method: 'DELETE',
    })
  },

  // Bulk delete tasks - sends a single request to delete multiple tasks
  bulkDeleteTasks: async (taskIds: string[]): Promise<{ deleted: number }> => {
    // See deleteTask: temp-keyed instant-add rows must be translated to their
    // real ids (and their sessions ended) before hitting the API.
    const resolvedIds = taskIds
      .map(resolveInstantAddDeleteTarget)
      .filter((id): id is string => id !== null)
    if (resolvedIds.length === 0) {
      return { deleted: 0 }
    }
    return apiClient<{ deleted: number }>('/tasks/bulk', {
      method: 'DELETE',
      body: { taskIds: resolvedIds },
    })
  },

  // Merge preview - per-task field values and content counts for the dialog
  previewMergeTasks: async (taskIds: string[]): Promise<MergeTasksPreview> => {
    return apiClient<MergeTasksPreview>('/tasks/merge/preview', {
      method: 'POST',
      body: { taskIds },
    })
  },

  // Merge tasks - re-points content to the primary, soft-deletes the rest
  mergeTasks: async (
    request: MergeTasksRequest,
  ): Promise<MergeTasksResponse> => {
    const response = await apiClient<MergeTasksResponse>('/tasks/merge', {
      method: 'POST',
      body: request,
    })
    return {
      ...response,
      primaryTask: normalizeTaskDates(response.primaryTask),
    }
  },

  // Bulk update tasks - sends one request for mass field changes
  bulkUpdateTasks: async (
    taskIds: string[],
    updates: UpdateTaskInput,
  ): Promise<TaskWithAssignments[]> => {
    const { statusComment, ...fields } = updates
    // A status in the update may require a comment, posted to each task.
    const send = (comment: string | undefined) =>
      apiClient<TaskWithAssignments[]>('/tasks/bulk/update', {
        method: 'POST',
        body: {
          taskIds,
          updates: comment ? { ...fields, statusComment: comment } : fields,
        },
      })
    const response =
      fields.status !== undefined
        ? await withStatusCommentRetry(send, {
            statusComment,
            taskCount: taskIds.length,
          })
        : await send(undefined)

    return normalizeTasksList(response)
  },

  bulkUpdateManyTasks: async (
    tasks: { taskId: string; updates: UpdateTaskInput }[],
  ): Promise<TaskWithAssignments[]> => {
    const movingCount = tasks.filter(
      entry => entry.updates.status !== undefined,
    ).length
    // Entries that change status and still lack a comment get the one the
    // person gives in the fallback prompt.
    const send = (comment: string | undefined) =>
      apiClient<TaskWithAssignments[]>('/tasks/bulk/update-many', {
        method: 'POST',
        body: {
          tasks: comment
            ? tasks.map(entry =>
                entry.updates.status !== undefined &&
                !entry.updates.statusComment
                  ? {
                      ...entry,
                      updates: { ...entry.updates, statusComment: comment },
                    }
                  : entry,
              )
            : tasks,
        },
      })
    const response =
      movingCount > 0
        ? await withStatusCommentRetry(send, { taskCount: movingCount })
        : await send(undefined)

    return normalizeTasksList(response)
  },

  // Bulk status update - preserves backend status transition behavior in one request
  bulkUpdateTaskStatus: async (
    taskIds: string[],
    statusId: string,
    statusTypeId: TaskStatusTypeEnum,
    options: { statusComment?: string } = {},
  ): Promise<TaskWithAssignments[]> => {
    // One comment, posted to every moved task, when the status requires it.
    const response = await withStatusCommentRetry(
      statusComment =>
        apiClient<TaskWithAssignments[]>('/tasks/bulk/status', {
          method: 'POST',
          body: { taskIds, statusId, statusTypeId, statusComment },
        }),
      { statusComment: options.statusComment, taskCount: taskIds.length },
    )

    trackFeatureUsed('bulk_task_status', { count: taskIds.length })
    if (statusTypeId === TaskStatusTypeEnum.COMPLETED) {
      trackTaskCompleted({ count: taskIds.length, source: 'bulk' })
      noteEngagementActivity('task_completed')
    }

    return normalizeTasksList(response)
  },

  bulkAssignTasks: async (
    taskIds: string[],
    userIds: string[],
  ): Promise<TaskWithAssignments[]> => {
    const response = await apiClient<TaskWithAssignments[]>(
      '/tasks/bulk/assign',
      {
        method: 'POST',
        body: { taskIds, userIds },
      },
    )

    return normalizeTasksList(response)
  },

  bulkUnassignTasks: async (
    taskIds: string[],
    userIds?: string[],
  ): Promise<TaskWithAssignments[]> => {
    const response = await apiClient<TaskWithAssignments[]>(
      '/tasks/bulk/unassign',
      {
        method: 'POST',
        body: { taskIds, userIds },
      },
    )

    return normalizeTasksList(response)
  },

  // Project recycle bin
  getProjectRecycleBin: async (
    projectId: string,
    params: PaginationParams = { page: 1, limit: 50 },
    options?: { signal?: AbortSignal },
  ): Promise<PaginatedResponse<TaskWithAssignments>> => {
    const queryParams = new URLSearchParams(
      cleanParams(params as unknown as Record<string, unknown>),
    )
    const queryString = queryParams.toString()
    const url = `/tasks/recycle-bin/${projectId}${queryString ? `?${queryString}` : ''}`

    const response = await apiClient<PaginatedResponse<TaskWithAssignments>>(
      url,
      {
        method: 'GET',
        signal: options?.signal,
      },
    )

    return {
      ...response,
      data: response.data.map(normalizeTaskDates),
    }
  },

  restoreProjectTask: async (
    projectId: string,
    taskId: string,
    options?: { signal?: AbortSignal },
  ): Promise<TaskWithAssignments> => {
    const response = await apiClient<TaskWithAssignments>(
      `/tasks/recycle-bin/${projectId}/${taskId}/restore`,
      {
        method: 'POST',
        signal: options?.signal,
      },
    )

    return normalizeTaskDates(response)
  },

  /** Deleted groups of a project, with the tasks deleted alongside each. */
  getProjectDeletedTaskGroups: (
    projectId: string,
    options?: { signal?: AbortSignal },
  ): Promise<DeletedTaskGroup[]> =>
    apiClient<DeletedTaskGroup[]>(`/task-groups/recycle-bin/${projectId}`, {
      method: 'GET',
      signal: options?.signal,
    }),

  /** Restores the group and the tasks that were deleted with it. */
  restoreProjectTaskGroup: (
    projectId: string,
    groupId: string,
    options?: { signal?: AbortSignal },
  ): Promise<RestoreTaskGroupResult> =>
    apiClient<RestoreTaskGroupResult>(
      `/task-groups/recycle-bin/${projectId}/${groupId}/restore`,
      { method: 'POST', signal: options?.signal },
    ),

  // My Tasks (private personal) recycle bin
  getMyTasksRecycleBin: async (
    params: PaginationParams = { page: 1, limit: 50 },
    options?: { signal?: AbortSignal },
  ): Promise<PaginatedResponse<TaskWithAssignments>> => {
    const queryParams = new URLSearchParams(
      cleanParams(params as unknown as Record<string, unknown>),
    )
    const queryString = queryParams.toString()
    const url = `/tasks/recycle-bin/my-tasks${queryString ? `?${queryString}` : ''}`

    const response = await apiClient<PaginatedResponse<TaskWithAssignments>>(
      url,
      {
        method: 'GET',
        signal: options?.signal,
      },
    )

    return {
      ...response,
      data: response.data.map(normalizeTaskDates),
    }
  },

  restoreMyPrivateTask: async (
    taskId: string,
    options?: { signal?: AbortSignal },
  ): Promise<TaskWithAssignments> => {
    const response = await apiClient<TaskWithAssignments>(
      `/tasks/recycle-bin/my-tasks/${taskId}/restore`,
      {
        method: 'POST',
        signal: options?.signal,
      },
    )

    return normalizeTaskDates(response)
  },

  // Get tasks for a module
  getModuleTasks: async (
    moduleType: ModuleTypeEnum,
    moduleId: string,
    params: PaginationParams & {
      includeCustomFields?: boolean
      search?: string
      assigneeIds?: string[]
      dueDateFrom?: string
      dueDateTo?: string
      scheduledForFrom?: string
      scheduledForTo?: string
      includeSubtasks?: boolean
    },
  ): Promise<PaginatedResponse<TaskWithAssignments>> => {
    return await tasksApi.getTasks({
      ...params,
      moduleType,
      moduleId,
      includeCustomFields: params.includeCustomFields !== false,
      view: 'list',
    })
  },

  // Task status operations
  updateTaskStatus: async (
    taskId: string,
    statusId: string,
    statusTypeId: TaskStatusTypeEnum,
    options: { statusComment?: string } = {},
  ): Promise<{
    task: TaskWithAssignments
    newRecurringInstance?: TaskWithAssignments
    newRecurringInstances?: TaskWithAssignments[]
  }> => {
    // Statuses can require a comment (SD-145): callers that know the status
    // pass it; otherwise the server's 400 opens the prompt and we retry once.
    const response = await withStatusCommentRetry(
      statusComment =>
        apiClient<{
          task: Task
          newRecurringInstance?: Task
          newRecurringInstances?: Task[]
        }>(`/tasks/${taskId}/status`, {
          method: 'PUT',
          body: { statusId, statusTypeId, statusComment },
        }),
      { statusComment: options.statusComment },
    )

    const result: {
      task: TaskWithAssignments
      newRecurringInstance?: TaskWithAssignments
      newRecurringInstances?: TaskWithAssignments[]
    } = {
      task: normalizeTaskDates(response.task),
    }

    if (response.newRecurringInstance) {
      result.newRecurringInstance = normalizeTaskDates(
        response.newRecurringInstance,
      )
    }

    if (response.newRecurringInstances?.length) {
      result.newRecurringInstances = response.newRecurringInstances.map(task =>
        normalizeTaskDates(task),
      )
    }

    // Only a transition INTO a completed status type counts as a completion —
    // every other status move is a workflow step, not a product outcome.
    if (statusTypeId === TaskStatusTypeEnum.COMPLETED) {
      trackTaskCompleted({
        module: result.task.moduleType ?? 'none',
        is_subtask: !!result.task.parentTaskId,
        is_recurring: !!(
          result.newRecurringInstance || result.newRecurringInstances?.length
        ),
        source: 'single',
      })
      noteEngagementActivity('task_completed')
    }

    return result
  },

  /**
   * Completed/canceled tasks per task group for `getTasks`-style filters —
   * what "Hide completed tasks" is holding back in each section.
   */
  getCompletedCounts: async (
    params: Record<string, unknown>,
    options?: { signal?: AbortSignal },
  ): Promise<TaskCompletedCounts> => {
    const cleanedParams = cleanParams(params)
    if (params.includeSubtasks !== true) {
      cleanedParams.excludeSubtasks = 'true'
    }
    return apiClient<TaskCompletedCounts>(
      `/tasks/completed-counts?${new URLSearchParams(cleanedParams)}`,
      { method: 'GET', signal: options?.signal },
    )
  },

  // Get task counts by status
  getTaskCountsByStatus: async (options?: {
    moduleType?: string
    moduleId?: string
  }): Promise<{ statusId: string; count: number; statusType: string }[]> => {
    let url = '/tasks/status-counts'
    if (options) {
      const queryParams = new URLSearchParams()
      Object.keys(options).forEach(key => {
        const value = options[key as keyof typeof options]
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value))
        }
      })
      const queryString = queryParams.toString()
      url = `${url}${queryString ? `?${queryString}` : ''}`
    }

    return await apiClient(url, {
      method: 'GET',
    })
  },

  // Field-specific update
  updateTaskField: async (
    taskId: string,
    field: string,
    value: unknown,
  ): Promise<TaskWithAssignments> => {
    let updateData: Record<string, unknown>

    if (field.startsWith('custom_')) {
      const fieldName = field.replace('custom_', '')
      updateData = {
        customFields: {
          [fieldName]: value,
        },
      }
    } else {
      updateData = {
        [field]: value,
      }
    }

    const send = (comment: string | undefined) =>
      apiClient<TaskWithAssignments>(`/tasks/${taskId}`, {
        method: 'PATCH',
        body: comment ? { ...updateData, statusComment: comment } : updateData,
      })
    // A status edit may require a comment (SD-145).
    const response =
      field === 'status'
        ? await withStatusCommentRetry(send)
        : await send(undefined)

    return normalizeTaskDates(response)
  },

  // Task assignment operations
  assignTaskToUser: async (
    taskId: string,
    userId: string,
  ): Promise<TaskWithAssignments> => {
    const response = await apiClient<TaskWithAssignments>(
      `/tasks/${taskId}/assign/${userId}`,
      {
        method: 'POST',
      },
    )

    return normalizeTaskDates(response)
  },

  assignTaskToMember: async (
    taskId: string,
    memberId: string,
  ): Promise<TaskWithAssignments> => {
    const response = await apiClient<TaskWithAssignments>(
      `/tasks/${taskId}/assign-member/${memberId}`,
      {
        method: 'POST',
      },
    )

    return normalizeTaskDates(response)
  },

  unassignTaskFromUser: async (
    taskId: string,
    userId: string,
  ): Promise<TaskWithAssignments> => {
    const response = await apiClient<TaskWithAssignments>(
      `/tasks/${taskId}/assign/${userId}`,
      {
        method: 'DELETE',
      },
    )

    return normalizeTaskDates(response)
  },

  unassignTaskFromMember: async (
    taskId: string,
    memberId: string,
  ): Promise<TaskWithAssignments> => {
    const response = await apiClient<TaskWithAssignments>(
      `/tasks/${taskId}/assign-member/${memberId}`,
      {
        method: 'DELETE',
      },
    )

    return normalizeTaskDates(response)
  },

  assignTaskToAgent: async (
    taskId: string,
    agentAccountId: string,
  ): Promise<TaskWithAssignments> => {
    const response = await apiClient<TaskWithAssignments>(
      `/tasks/${taskId}/assign-agent/${agentAccountId}`,
      {
        method: 'POST',
      },
    )

    return normalizeTaskDates(response)
  },

  unassignTaskFromAgent: async (
    taskId: string,
    agentAccountId: string,
  ): Promise<TaskWithAssignments> => {
    const response = await apiClient<TaskWithAssignments>(
      `/tasks/${taskId}/assign-agent/${agentAccountId}`,
      {
        method: 'DELETE',
      },
    )

    return normalizeTaskDates(response)
  },

  getTaskAssignments: async (
    taskId: string,
  ): Promise<
    {
      id: string
      taskId: string
      userId: string
      assignedAt: Date
      user: { id: string; name: string; email: string }
    }[]
  > => {
    return await apiClient(`/tasks/${taskId}/assignments`, {
      method: 'GET',
    })
  },

  getAssignablePortalMembers: async (
    taskId: string,
    options?: { signal?: AbortSignal },
  ): Promise<AssignablePortalMembers> => {
    return await apiClient<AssignablePortalMembers>(
      `/tasks/${taskId}/assignable-portal-members`,
      { ...options, method: 'GET' },
    )
  },

  // Bulk operations
  bulkAssignToTask: async (
    taskId: string,
    userIds: string[],
  ): Promise<TaskWithAssignments> => {
    const response = await apiClient<TaskWithAssignments>(
      `/tasks/${taskId}/assign/bulk`,
      {
        method: 'POST',
        body: { userIds },
      },
    )

    return normalizeTaskDates(response)
  },

  bulkUnassignFromTask: async (
    taskId: string,
    userIds: string[],
  ): Promise<TaskWithAssignments> => {
    const response = await apiClient<TaskWithAssignments>(
      `/tasks/${taskId}/assign/bulk`,
      {
        method: 'DELETE',
        body: { userIds },
      },
    )

    return normalizeTaskDates(response)
  },

  bulkChangeGroup: async (
    taskIds: string[],
    groupId: string | null,
  ): Promise<TaskWithAssignments[]> => {
    const response = await apiClient<TaskWithAssignments[]>(
      '/tasks/bulk/change-group',
      {
        method: 'POST',
        body: { taskIds, groupId },
      },
    )

    return normalizeTasksList(response)
  },

  // Subtask operations
  getSubtasks: async (taskId: string): Promise<TaskWithAssignments[]> => {
    const url = `/tasks/${taskId}/subtasks`
    const response = await apiClient<TaskWithAssignments[]>(url, {
      method: 'GET',
    })
    return normalizeTasksList(response)
  },

  // Recurring task operations
  getRecurringTaskInstances: async (
    parentTaskId: string,
  ): Promise<TaskWithAssignments[]> => {
    const response = await apiClient<TaskWithAssignments[]>(
      `/tasks/${parentTaskId}/recurring-instances`,
      {
        method: 'GET',
      },
    )

    return normalizeTasksList(response)
  },

  stopRecurringSeries: async (taskId: string): Promise<TaskWithAssignments> => {
    const response = await apiClient<TaskWithAssignments>(
      `/tasks/${taskId}/stop-recurring`,
      {
        method: 'POST',
      },
    )

    return normalizeTaskDates(response)
  },

  updateRecurringPattern: async (
    taskId: string,
    pattern: RecurringPattern,
  ): Promise<TaskWithAssignments> => {
    const response = await apiClient<Task>(
      `/tasks/${taskId}/recurring-pattern`,
      {
        method: 'PUT',
        body: { recurringPattern: pattern },
      },
    )

    return normalizeTaskDates(response)
  },

  // Task ordering
  reorderTasks: async (
    taskOrders: Array<{
      taskId: string
      order: string
      groupId?: string | null
    }>,
  ): Promise<{ success: boolean; message: string }> => {
    return await apiClient<{ success: boolean; message: string }>(
      '/tasks/reorder',
      {
        method: 'POST',
        body: { taskOrders },
      },
    )
  },

  reorderSprintTasks: async (
    sprintId: string,
    taskOrders: Array<{
      taskId: string
      order?: string
      sprintOrder?: number
    }>,
  ): Promise<{ success: boolean; message: string }> => {
    return await apiClient<{ success: boolean; message: string }>(
      `/sprints/${sprintId}/tasks/reorder`,
      {
        method: 'POST',
        body: { taskOrders },
      },
    )
  },

  // Task groups
  getTaskGroups: async (params: {
    moduleType?: ModuleTypeEnum
    moduleId?: string
    sortBy?: string
    sortOrder?: 'asc' | 'desc'
    page?: number
    limit?: number
  }): Promise<PaginatedTaskGroupResponse> => {
    // Guard against unsupported temp TASK module context during optimistic flows
    const safeParams = { ...params }
    if (
      safeParams.moduleType === ModuleTypeEnum.TASK &&
      typeof safeParams.moduleId === 'string' &&
      safeParams.moduleId.startsWith('temp-')
    ) {
      // Drop module scoping; fall back to org/project default groups to avoid 500s
      delete safeParams.moduleType
      delete safeParams.moduleId
    }

    const queryParams = new URLSearchParams()
    Object.keys(safeParams).forEach(key => {
      const value = safeParams[key as keyof typeof safeParams]
      if (value !== undefined && value !== null) {
        queryParams.append(key, String(value))
      }
    })
    const queryString = queryParams.toString()
    const url = `/task-groups${queryString ? `?${queryString}` : ''}`

    const response = await apiClient<PaginatedTaskGroupResponse>(url, {
      method: 'GET',
    })

    return response
  },

  getTaskGroupsWithCounts: async (params: {
    moduleType?: ModuleTypeEnum
    moduleId?: string
    sortBy?: string
    sortOrder?: 'asc' | 'desc'
    page?: number
    limit?: number
  }): Promise<PaginatedTaskGroupWithCountsResponse> => {
    const safeParams = { ...params }
    if (
      safeParams.moduleType === ModuleTypeEnum.TASK &&
      typeof safeParams.moduleId === 'string' &&
      safeParams.moduleId.startsWith('temp-')
    ) {
      delete safeParams.moduleType
      delete safeParams.moduleId
    }

    const queryParams = new URLSearchParams()
    Object.keys(safeParams).forEach(key => {
      const value = safeParams[key as keyof typeof safeParams]
      if (value !== undefined && value !== null) {
        queryParams.append(key, String(value))
      }
    })
    const queryString = queryParams.toString()
    const url = `/task-groups/with-counts${queryString ? `?${queryString}` : ''}`

    const response = await apiClient<PaginatedTaskGroupWithCountsResponse>(
      url,
      {
        method: 'GET',
      },
    )

    return response
  },

  createTaskGroup: async (data: {
    name: string
    color?: string
    description?: string
    startDate?: string
    endDate?: string
    moduleType?: ModuleTypeEnum
    moduleId?: string
    defaultTaskTypeDefId?: string | null
    allowedTaskTypeDefIds?: string[]
  }): Promise<TaskGroup> => {
    const response = await apiClient<TaskGroup>('/task-groups', {
      method: 'POST',
      body: data,
    })

    return response
  },

  updateTaskGroup: async (
    id: string,
    data: {
      name?: string
      color?: string
      description?: string
      order?: string
      defaultTaskTypeDefId?: string | null
      allowedTaskTypeDefIds?: string[]
    },
  ): Promise<TaskGroup> => {
    const response = await apiClient<TaskGroup>(`/task-groups/${id}`, {
      method: 'PUT',
      body: data,
    })

    return response
  },

  reorderTaskGroups: async (
    groupOrders: Array<{ groupId: string; order: string }>,
  ): Promise<{ success: boolean; message: string }> => {
    return await apiClient<{ success: boolean; message: string }>(
      '/task-groups/reorder',
      {
        method: 'POST',
        body: { groupOrders },
      },
    )
  },

  deleteTaskGroup: async (
    id: string,
    deleteMode?: 'delete-tasks' | 'move-to-unassigned',
  ): Promise<void> => {
    const url = deleteMode
      ? `/task-groups/${id}?deleteMode=${deleteMode}`
      : `/task-groups/${id}`

    await apiClient(url, {
      method: 'DELETE',
    })
  },

  // Task group move/duplicate operations
  getTaskGroupMovePreview: async (
    groupId: string,
    targetProjectId: string,
  ): Promise<MovePreview> => {
    return await apiClient<MovePreview>(
      `/task-groups/${groupId}/move-preview?targetProjectId=${targetProjectId}`,
      { method: 'GET' },
    )
  },

  moveTaskGroupToProject: async (
    groupId: string,
    data: MoveTaskGroup,
  ): Promise<MoveResult> => {
    return await apiClient<MoveResult>(`/task-groups/${groupId}/move`, {
      method: 'POST',
      body: data,
    })
  },

  duplicateTaskGroupToProject: async (
    groupId: string,
    data: DuplicateTaskGroupToProject,
  ): Promise<DuplicateResult> => {
    return await apiClient<DuplicateResult>(
      `/task-groups/${groupId}/duplicate-to-project`,
      {
        method: 'POST',
        body: data,
      },
    )
  },

  // Import/Export operations
  previewImport: async (
    records: unknown[],
    options: ImportOptions,
  ): Promise<ImportPreviewRecord[]> => {
    logger.info('[previewImport] Starting preview', {
      recordsCount: records.length,
      options,
      sampleRecord: records[0]
        ? JSON.stringify(records[0]).substring(0, 300)
        : null,
    })
    try {
      const result = await apiClient<ImportPreviewRecord[]>(
        '/tasks/import/preview',
        {
          method: 'POST',
          body: { records, options },
        },
      )
      logger.info('[previewImport] Preview completed', {
        resultCount: result.length,
      })
      return result
    } catch (error) {
      logger.error('[previewImport] Error', {
        error,
        errorMessage: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? error.stack : undefined,
      })
      throw error
    }
  },

  import: async (
    records: ImportPreviewRecord[],
    options: ImportOptions,
  ): Promise<ImportResult> => {
    logger.info('[import] Starting import', {
      recordsCount: records.length,
      options,
      sampleRecord: records[0]
        ? {
            rowIndex: records[0].rowIndex,
            action: records[0].action,
            hasData: !!records[0].data,
            dataKeys: records[0].data ? Object.keys(records[0].data) : [],
          }
        : null,
    })
    try {
      const result = await apiClient<ImportResult>('/tasks/import', {
        method: 'POST',
        body: { records, options },
      })
      logger.info('[import] Import completed', { result })
      return result
    } catch (error) {
      logger.error('[import] Error', {
        error,
        errorMessage: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? error.stack : undefined,
      })
      throw error
    }
  },

  importBatch: async (batchRequest: {
    batchId: string
    batchIndex: number
    totalBatches: number
    records: ImportPreviewRecord[]
    options: ImportOptions
    isFirstBatch?: boolean
    isLastBatch?: boolean
  }): Promise<{
    batchId: string
    batchIndex: number
    totalBatches: number
    successCount: number
    errorCount: number
    errors?: string[]
    overallProgress: {
      completed: number
      total: number
      percentage: number
    }
  }> => {
    return await apiClient('/tasks/import/batch', {
      method: 'POST',
      body: batchRequest,
    })
  },

  export: async (options: ExportOptions): Promise<Task[]> => {
    return await apiClient<Task[]>('/tasks/export', {
      method: 'POST',
      body: options,
    })
  },

  countForExport: async (
    options: Pick<ExportOptions, 'filters' | 'selectedIds' | 'projectId'>,
  ): Promise<{ total: number }> => {
    return await apiClient<{ total: number }>('/tasks/export/count', {
      method: 'POST',
      body: options,
    })
  },

  getTasksForSprintBoard: async (
    sprintId: string,
  ): Promise<{ sprintId: string; tasks: TaskWithAssignments[] }> => {
    const response = await apiClient<{
      sprintId: string
      tasks: TaskWithAssignments[]
    }>(`/sprints/${sprintId}/tasks-minimal`, {
      method: 'GET',
    })
    return response
  },

  bulkCreateTasksWithGroups: async (data: {
    projectId: string
    groups: Array<{
      id?: string
      name: string
      description?: string
      color: string
      isExisting?: boolean
      existingGroupId?: string
      tasks: Array<{
        name: string
        description?: string
        priority?: string
        estimatedHours?: number
        taskTypeDefId?: string | null
      }>
    }>
    tasks: Array<{
      name: string
      description?: string
      priority?: string
      estimatedHours?: number
      groupId?: string
      taskTypeDefId?: string | null
    }>
  }): Promise<{
    createdGroups: Array<{ id: string; name: string }>
    createdTasks: Task[]
  }> => {
    const response = await apiClient<{
      createdGroups: Array<{ id: string; name: string }>
      createdTasks: Task[]
    }>('/tasks/bulk/create-with-groups', {
      method: 'POST',
      body: data,
    })

    trackTaskCreated(
      { moduleType: ModuleTypeEnum.PROJECT, parentTaskId: null },
      { count: response.createdTasks?.length ?? 0, source: 'bulk' },
    )

    return response
  },

  // ----------------------------- approvals -----------------------------
  getApprovalHistory: async (
    id: string,
    options?: { signal?: AbortSignal },
  ): Promise<TaskApprovalEntry[]> =>
    apiClient<TaskApprovalEntry[]>(`/tasks/${id}/approval/history`, {
      signal: options?.signal,
    }),

  approve: async (
    id: string,
    reason?: string,
    options?: { expectedRevision?: number; signal?: AbortSignal },
  ): Promise<Task> => {
    const task = await apiClient<Task>(`/tasks/${id}/approval/approve`, {
      method: 'POST',
      body: { reason, expectedRevision: options?.expectedRevision },
      signal: options?.signal,
    })
    assertTaskWriteAcknowledgement(task, id, options?.expectedRevision)
    return task
  },

  reject: async (
    id: string,
    reason?: string,
    options?: { expectedRevision?: number; signal?: AbortSignal },
  ): Promise<Task> => {
    const task = await apiClient<Task>(`/tasks/${id}/approval/reject`, {
      method: 'POST',
      body: { reason, expectedRevision: options?.expectedRevision },
      signal: options?.signal,
    })
    assertTaskWriteAcknowledgement(task, id, options?.expectedRevision)
    return task
  },

  requestApprovalChanges: async (
    id: string,
    reason?: string,
    options?: { expectedRevision?: number; signal?: AbortSignal },
  ): Promise<Task> => {
    const task = await apiClient<Task>(
      `/tasks/${id}/approval/request-changes`,
      {
        method: 'POST',
        body: { reason, expectedRevision: options?.expectedRevision },
        signal: options?.signal,
      },
    )
    assertTaskWriteAcknowledgement(task, id, options?.expectedRevision)
    return task
  },

  /**
   * Tasks report PDF for one project.
   *
   * Routed through `requestBlob` rather than a hand-rolled `fetch` so the
   * desktop app sends its Bearer token — cookie auth alone 401s there.
   */
  exportProjectPdf: async (projectId: string, compact = false): Promise<Blob> =>
    requestBlob(
      `/tasks/export/pdf/${projectId}${compact ? '?compact=true' : ''}`,
      { acceptType: 'application/pdf' },
    ),

  /** Tasks report PDF across the current filter set. */
  exportPdf: async (body: {
    filters?: unknown
    title?: string
    compactMode?: boolean
  }): Promise<Blob> =>
    requestBlob('/tasks/export/pdf', {
      method: 'POST',
      acceptType: 'application/pdf',
      body,
    }),
}
